import React, { useMemo, useState } from "react";
import moment from "moment";
import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  SalesAnalyticsOverflowBox,
  StyledSalesAnalyticsCard,
  SemiBoldText,
} from "../styles";

import { Box, Grid, InputAdornment, TextField } from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

import { useOutletContext } from "react-router-dom";
import CustomDropdown from "../../../../common/CustomDropdown";
import colors from "../../../../theme/Colours";
import { ActionButton } from "../../../../common/actionButton/ActionButton";
import Toast from "../../../../common/Toast";
import { exportToExcel } from "../../../../utils/exportToExcel";
import { dashboardAPI } from "../../../../api/kpiCardsAPI";

type ExcelRow = {
  "Outlet Name": string;
  "Order id": string;
  "Invoice Number": string;
  "Total  Bill Amount": number;
  "Amount Paid": number;
  "Outstanding Amount": number;
  "Aging days": number;
};

type OutstandingAgeingReportRow = {
  serial_id: number;
  outlet_name: string;
  order_id: string;
  invoice_number: string;
  total_bill_amount: number;
  amount_paid: number;
  outstanding_amount: number;
  ageing_days: number;
};

type OutletContextType = {
  globalFilters?: any;
  dashboardType?: string;
  sellerEmail?: string;
  customerEmail?: string;
};

const OutstandingAgeingReport = () => {
  const { globalFilters, dashboardType, sellerEmail, customerEmail } =
    useOutletContext<OutletContextType>();

  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);

  const [ageingBucket, setAgeingBucket] = useState<string>("");

  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadEnabled, setDownloadEnabled] = useState(false);
  const [reportData, setReportData] = useState<ExcelRow[]>([]);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastIsError, setToastIsError] = useState(false);

  const ageingBucketOptions = useMemo(
    () => [
      { value: "0-30", label: "0 - 30 Days" },
      { value: "30-60", label: "30 - 60 Days" },
      { value: "60-90", label: "60 - 90 Days" },
      { value: "90+", label: "90+ Days" },
    ],
    []
  );

  const showToastMsg = (msg: string, isError = false) => {
    setToastMessage(msg);
    setToastIsError(isError);
    setShowToast(true);
  };

  const mapToExcelRows = (rows: OutstandingAgeingReportRow[]): ExcelRow[] =>
    rows.map((r) => ({
      "Outlet Name": r.outlet_name,
      "Order id": r.order_id,
      "Invoice Number": r.invoice_number,
      "Total  Bill Amount": r.total_bill_amount,
      "Amount Paid": r.amount_paid,
      "Outstanding Amount": r.outstanding_amount,
      "Aging days": r.ageing_days,
    }));

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    setDownloadEnabled(false);
    setReportData([]);

    if (!selectedDate || !ageingBucket) {
      showToastMsg("Please select Date and Ageing Bucket", true);
      setIsGenerating(false);
      return;
    }

    try {
      const dateValue = selectedDate.format("YYYY-MM-DD");

      const res = await dashboardAPI.getOutstandingAgeingReport(
        globalFilters,
        dashboardType ?? "finance-analytics",
        dateValue,
        ageingBucket,
        sellerEmail,
        customerEmail
      );

      const rows: OutstandingAgeingReportRow[] = Array.isArray(res?.data)
        ? res.data
        : [];

      if (!rows.length) {
        showToastMsg("No data found", true);
        setSelectedDate(null);
        setAgeingBucket("");
        setDownloadEnabled(false);
        setReportData([]);
        setIsGenerating(false);
        return;
      }

      const excelRows = mapToExcelRows(rows);

      setReportData(excelRows);
      setDownloadEnabled(true);
      showToastMsg("Your report has been generated successfully", false);
    } catch (err) {
      console.error("Outstanding Ageing Report error:", err);
      showToastMsg("No data found", true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!reportData.length) return;

    const d = selectedDate ? selectedDate.format("YYYYMMDD") : "NA";
    await exportToExcel(
      reportData,
      `Outstanding_Ageing_Report_${d}_${ageingBucket}.xlsx`,
      "Outstanding Ageing"
    );

    setAgeingBucket("");
    setSelectedDate(null);
    setDownloadEnabled(false);
    setReportData([]);
  };

  return (
    <StyledSalesAnalyticsCard
      sx={{ display: "flex", flexDirection: "column", height: "330px" }}
    >
      <SalesAnalyticsCardLabel>
        Outstanding Ageing Report
      </SalesAnalyticsCardLabel>

      <SalesAnalyticsContentBox
        sx={{ pl: 0, display: "flex", flexDirection: "column", flex: 1 }}
      >
        <SalesAnalyticsOverflowBox
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflowY: "unset",
          }}
        >
          <Grid container spacing={6} mb={6}>
            {/* Date */}
            <Grid item xs={12} md={6}>
              <SemiBoldText sx={{ fontSize: "14px", mb: 1, mt: 2 }}>
                Date
              </SemiBoldText>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MuiDatePicker
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  format="DD/MM/YYYY"
                  disableFuture
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "DD/MM/YYYY",
                      onClick: (e) => {
                        (e.currentTarget as any)
                          .querySelector("button")
                          ?.click();
                      },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    },
                    openPickerButton: {
                      sx: {
                        position: "absolute",
                        right: 0,
                        opacity: 0,
                        width: 1,
                        height: 1,
                        p: 0,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 44,
                      border: `1px solid ${colors.datePickerBorder}`,
                      borderRadius: "6px",
                      backgroundColor: colors.background2,
                      position: "relative",
                    },
                    "& fieldset": { border: "none" },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Ageing Bucket */}
            <Grid item xs={12} md={6}>
              <SemiBoldText sx={{ fontSize: "14px", mb: 1, mt: 2 }}>
                Ageing Bucket
              </SemiBoldText>

              <Box sx={{ width: "100%", height: 44 }}>
                <CustomDropdown
                  label="Select Bucket"
                  value={ageingBucket}
                  options={ageingBucketOptions}
                  onChange={setAgeingBucket}
                  customStyles={{
                    button: {
                      width: "100%",
                      height: "44px",
                      borderRadius: "6px",
                      border: `1px solid ${colors.datePickerBorder}`,
                      background: colors.background2,
                      padding: "10px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    },
                    label: {
                      fontSize: "15px",
                      fontWeight: 600,
                      color: colors.searchInputText,
                    },
                    menu: {
                      width: "75%",
                      right: 0,
                      left: "auto",
                      borderColor: colors.datePickerBorder,
                    },
                    option: {
                      height: "16px",
                      padding: "0 16px",
                      margin: "6px 0px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Bottom Buttons Row pinned to bottom */}
          <Box sx={{ mt: "auto", pb: 1 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <ActionButton
                  label={isGenerating ? "Generating..." : "Generate Report"}
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "10px !important",
                    fontWeight: 600,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ActionButton
                  label="Download Report"
                  onClick={handleDownload}
                  outlined
                  disabled={!downloadEnabled}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "10px !important",
                    fontWeight: 600,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </SalesAnalyticsOverflowBox>
      </SalesAnalyticsContentBox>

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={toastIsError}
      />
    </StyledSalesAnalyticsCard>
  );
};

export default OutstandingAgeingReport;
